import type {
  SavingsAttributionCancelPayload,
  SavingsAttributionChangePayload,
  SavingsCustomerAttributionState,
  SavingsCustomerDetail,
  SavingsCustomerListItem,
  SavingsCustomerManagers,
  SavingsCustomerPayload,
  SavingsCustomerSaved
} from '@/interfaces/SavingsCustomer.interface';
import type { SavingsCustomerPortfolios } from '@/interfaces/SavingsPortfolio.interface';
import BaseApiClient from '../BaseApiClient';
import {
  SAVINGS_API_VERSION_HEADERS,
  toSavingsListResponse
} from '../savings-reference-resource';
import type { ListDataParams, ListDataResponse } from '../types';

const PATH = '/savings-customers';

export class ApiSavingsCustomers extends BaseApiClient {
  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<SavingsCustomerListItem>> {
    const search = searchTerm?.trim();
    const res = await this.client.getList(
      PATH,
      {
        ...listData,
        filters: { ...listData.filters, ...(search ? { search } : {}) }
      },
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return toSavingsListResponse<SavingsCustomerListItem>(res.data);
  }

  async get(id: number | string): Promise<SavingsCustomerDetail> {
    const res = await this.client.get(`${PATH}/${id}`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  /** The customer's portfolio dashboards, one per Hub (FLT-2073). */
  async portfolios(id: number | string): Promise<SavingsCustomerPortfolios> {
    const res = await this.client.get(`${PATH}/${id}/portfolios`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  /** The advisers who manage the customer and its primary manager (FLT-2077). */
  async managers(id: number | string): Promise<SavingsCustomerManagers> {
    const res = await this.client.get(`${PATH}/${id}/managers`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async addManager(
    id: number,
    individualId: number
  ): Promise<SavingsCustomerManagers> {
    const res = await this.client.post(
      `${PATH}/${id}/managers`,
      { individual_id: individualId },
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data;
  }

  async removeManager(
    id: number,
    managerId: number
  ): Promise<SavingsCustomerManagers> {
    const res = await this.client.delete(
      `${PATH}/${id}/managers/${managerId}`,
      {
        headers: SAVINGS_API_VERSION_HEADERS
      }
    );
    return res.data.data;
  }

  /** Pass null to leave the customer with no primary manager. */
  async setPrimaryManager(
    id: number,
    managerId: number | null
  ): Promise<SavingsCustomerManagers> {
    const res = await this.client.put(
      `${PATH}/${id}/primary-manager`,
      { manager_id: managerId },
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data;
  }

  async setPrimaryAdviser(
    id: number,
    individualId: number
  ): Promise<SavingsCustomerManagers> {
    const res = await this.client.put(
      `${PATH}/${id}/primary-manager`,
      { individual_id: individualId },
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data;
  }

  async create(payload: SavingsCustomerPayload): Promise<SavingsCustomerSaved> {
    const res = await this.client.post(PATH, payload, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async update(
    id: number,
    payload: SavingsCustomerPayload
  ): Promise<SavingsCustomerSaved> {
    const res = await this.client.put(`${PATH}/${id}`, payload, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async getAttribution(
    id: number | string
  ): Promise<SavingsCustomerAttributionState> {
    const res = await this.client.get(`${PATH}/${id}/introducer-history`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async scheduleAttribution(
    id: number,
    payload: SavingsAttributionChangePayload
  ): Promise<SavingsCustomerAttributionState> {
    const res = await this.client.post(
      `${PATH}/${id}/introducer-history`,
      payload,
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data;
  }

  async cancelAttribution(
    id: number,
    historyId: number,
    payload: SavingsAttributionCancelPayload
  ): Promise<SavingsCustomerAttributionState> {
    const res = await this.client.delete(
      `${PATH}/${id}/introducer-history/${historyId}`,
      { headers: SAVINGS_API_VERSION_HEADERS, data: payload }
    );
    return res.data.data;
  }
}

export { savingsCustomersQueryKeys } from './query-keys';
export {
  useSavingsCustomerManagersQuery,
  useSavingsCustomerPortfoliosQuery,
  useSavingsCustomerQuery
} from './use-savings-customers-queries';
export {
  useSavingsCustomersCancelAttributionMutation,
  useSavingsCustomerManagersMutation,
  useSavingsCustomersCreateMutation,
  useSavingsCustomersScheduleAttributionMutation,
  useSavingsCustomersUpdateMutation
} from './use-savings-customers-mutations';
export type {
  SavingsCustomerManagersAction,
  SavingsCustomersCancelAttributionVariables,
  SavingsCustomersScheduleAttributionVariables,
  SavingsCustomersUpdateVariables
} from './use-savings-customers-mutations';
